import React, { useState } from 'react';
import { Button, DatePicker, HStack, parseDate, Text, VStack } from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import { datePickerPopupStyles } from '@/components/common/access-time-selector/styles.ts';
import { CONTROL_SURFACE_SHADOW } from '@/components/ui/shadows.ts';
import { toIsoDate } from '@/utils/dateInputUtils.ts';

interface MaintenanceDateChoiceFieldProps {
	/** ISO yyyy-mm-dd currently chosen, or '' when nothing has been picked yet. */
	value: string;
	onChange: (value: string) => void;
	/** Names the choice for a screen reader once a date is showing — "Change selected date, currently …". */
	changeLabel: string;
	/**
	 * Asked once a date is picked, in the room the collapsed calendar leaves behind.
	 *
	 * Passing one is what collapses the calendar at all: a sheet with nothing to ask keeps it open,
	 * since folding it away would only hide the control the sheet exists for.
	 *
	 * It is rendered either way and told whether the date is settled, rather than mounted at the
	 * moment it becomes relevant — a question that mounts already-open skips its own animation. So
	 * reopening the calendar collapses the question rather than unmounting it: the answer belongs to
	 * the date that prompted it, and that date is being reconsidered.
	 */
	followUp?: (isDateSettled: boolean) => React.ReactNode;
	/** Whether the current date is a choice the sheet can submit. */
	canSettle?: boolean;
	/** Earliest selectable ISO date, inclusive. */
	min?: string;
}

/**
 * The drawer's one date choice: an inline calendar that becomes the date it chose.
 *
 * Inline rather than a trigger and a popover, so picking a date is one interaction inside a sheet
 * that is already a modal surface. Shared by every sheet that asks for a date — rescheduling and
 * snoozing differ in what the date means, not in how it is chosen.
 */
const DateChoiceField: React.FC<MaintenanceDateChoiceFieldProps> = ({
	value,
	onChange,
	changeLabel,
	followUp,
	canSettle = true,
	min,
}) => {
	const [isChoosingDate, setIsChoosingDate] = useState(true);
	const dateValue = value ? [parseDate(value)] : [];
	const selectedDateLabel = value ? format(parseISO(value), 'd MMMM yyyy') : null;
	const isDateSettled = followUp !== undefined && canSettle && !isChoosingDate && selectedDateLabel !== null;

	if (isDateSettled) {
		return (
			<VStack align="stretch" gap={3} flex="1" minH={0}>
				<Button
					variant="outline"
					h={10}
					w="full"
					px={4}
					borderRadius="2xl"
					borderColor="brand.solid"
					bg="brand.solid"
					color="brand.contrast"
					boxShadow={CONTROL_SURFACE_SHADOW}
					_hover={{ bg: 'brand.solidHover', color: 'brand.contrast' }}
					onClick={() => setIsChoosingDate(true)}
					aria-label={`${changeLabel}, currently ${selectedDateLabel}`}
				>
					<HStack w="full" justify="space-between">
						<Text textStyle="label">{selectedDateLabel}</Text>
						<Text fontSize="xs">Change</Text>
					</HStack>
				</Button>
				{followUp?.(true)}
			</VStack>
		);
	}

	return (
		<VStack align="stretch" gap={3} flex="1" minH={0}>
			<DatePicker.Root
				inline
				min={min ? parseDate(min) : undefined}
				defaultFocusedValue={min ? parseDate(min) : undefined}
				value={dateValue}
				onValueChange={(details) => {
					const selected = details.value[0];
					onChange(selected ? toIsoDate(selected.year, selected.month, selected.day) : '');
					if (selected) setIsChoosingDate(false);
				}}
			>
				<DatePicker.Content {...datePickerPopupStyles} w="full" boxShadow="none">
					<DatePicker.View view="day">
						<DatePicker.Header />
						<DatePicker.DayTable />
					</DatePicker.View>
					<DatePicker.View view="month">
						<DatePicker.Header />
						<DatePicker.MonthTable />
					</DatePicker.View>
					<DatePicker.View view="year">
						<DatePicker.Header />
						<DatePicker.YearTable />
					</DatePicker.View>
				</DatePicker.Content>
			</DatePicker.Root>
			{followUp?.(false)}
		</VStack>
	);
};

export default DateChoiceField;
